import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, catchError, of } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  HealthCheckResult,
  SystemStatus,
  ModuleStatus,
  LocalizationMap
} from '../models/system-health.models';

export interface UserStats {
  total: number;
  active: number;
  blocked: number;
}

@Injectable({
  providedIn: 'root'
})
export class SystemHealthService {
  private readonly apiUrl = `${environment.api}/common/HealthMonitoring/GetHealthCheck`;
  private readonly userApiUrl = `${environment.api}/identity/User/GetByQuery`;

  constructor(private http: HttpClient) {}

  /**
   * Получает данные о состоянии системы с API
   */
  getHealthCheck(): Observable<HealthCheckResult[]> {
    return this.http.get<HealthCheckResult[]>(this.apiUrl).pipe(
      catchError(error => {
        console.error('Error fetching health check data:', error);
        return of([]);
      })
    );
  }

  /**
   * Получает статус системы с обработкой данных
   */
  getSystemStatus(): Observable<SystemStatus> {
    return this.getHealthCheck().pipe(
      map(data => {
        if (data.length === 0) {
          return {
            overallStatus: 'error' as const,
            modulesWithErrors: 0,
            totalErrors: 0,
            modules: []
          };
        }

        return this.transformHealthData(data);
      }),
      catchError(error => {
        console.error('Error processing health check data:', error);
        return of({
          overallStatus: 'error' as const,
          modulesWithErrors: 0,
          totalErrors: 0,
          modules: []
        });
      })
    );
  }

  /**
   * Трансформирует данные API в структуру для отображения
   */
   transformHealthData(data: HealthCheckResult[]): SystemStatus {
     const sortedData = data.sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime());
     const latest = sortedData[0]; // Get the most recent record by date

     if (!latest) {
       return {
         overallStatus: 'error',
         modulesWithErrors: 0,
         totalErrors: 0,
         modules: []
       };
     }

     // Count modules with errors
     const modulesWithErrors = latest.modules.filter(m =>
       m.errors.length > 0 || (m.models && m.models.some(model => model.errors.length > 0))
     ).length;

     // Count total number of errors
     const totalErrors = latest.errors.length +
       latest.modules.reduce((sum, m) =>
         sum + m.errors.length + (m.models ? m.models.reduce((modelSum, model) =>
           modelSum + model.errors.length, 0
         ) : 0), 0
       );

     // Create module statuses
     const modules: ModuleStatus[] = latest.modules.map(m => {
       const moduleErrors = m.errors.length;
       const modelErrors = m.models ? m.models.reduce((sum, model) => sum + model.errors.length, 0) : 0;
       const totalModuleErrors = moduleErrors + modelErrors;
       const moduleNames = this.extractLocalization(m, 'moduleNames');
       const localizedErrors = this.extractLocalizationArray(m, 'localizedErrors');

       return {
         name: m.moduleName || 'Unknown Module',
         status: totalModuleErrors > 0 ? 'error' : 'healthy',
         errorCount: totalModuleErrors,
         moduleNames,
         localizedErrors: localizedErrors ?? undefined
       };
     });

     // Determine overall status
    let overallStatus: 'healthy' | 'warning' | 'error' = 'healthy';
    if (totalErrors > 0) {
      overallStatus = 'error';
    } else if (modulesWithErrors > 0) {
      overallStatus = 'warning';
    }

    return {
      overallStatus,
      modulesWithErrors,
      totalErrors,
      modules
    };
  }

  /**
   * Получает количество модулей с ошибками
   */
  getModulesWithErrorsCount(): Observable<number> {
    return this.getHealthCheck().pipe(
      map(data => {
        if (data.length === 0) return 0;
        const sortedData = data.sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime());
        const latest = sortedData[0];
        return latest.modules.filter(m =>
          m.errors.length > 0 || (m.models && m.models.some(model => model.errors.length > 0))
        ).length;
      })
    );
  }

  /**
   * Получает общее количество ошибок
   */
  getTotalErrorsCount(): Observable<number> {
    return this.getHealthCheck().pipe(
      map(data => {
        if (data.length === 0) return 0;
        const sortedData = data.sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime());
        const latest = sortedData[0];
        return latest.errors.length +
          latest.modules.reduce((sum, m) =>
            sum + m.errors.length + (m.models ? m.models.reduce((modelSum, model) =>
              modelSum + model.errors.length, 0
            ) : 0), 0
          );
      })
    );
  }

  /**
   * Получает статус конкретного модуля
   */
  getModuleStatus(moduleName: string): Observable<ModuleStatus | null> {
    return this.getHealthCheck().pipe(
      map(data => {
        if (data.length === 0) return null;
        const sortedData = data.sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime());
        const latest = sortedData[0];
        const module = latest.modules.find(m => m.moduleName === moduleName);

        if (!module) return null;

        const moduleErrors = module.errors.length;
        const modelErrors = module.models ? module.models.reduce((sum, model) => sum + model.errors.length, 0) : 0;
        const totalModuleErrors = moduleErrors + modelErrors;
        const moduleNames = this.extractLocalization(module, 'moduleNames');
        const localizedErrors = this.extractLocalizationArray(module, 'localizedErrors');

        return {
          name: module.moduleName || 'Unknown Module',
          status: totalModuleErrors > 0 ? 'error' : 'healthy',
          errorCount: totalModuleErrors,
          moduleNames,
          localizedErrors: localizedErrors ?? undefined
        };
      })
    );
  }

   /**
    * Get user statistics
    */
   getUserStats(): Observable<UserStats> {
     return this.http.post<any[]>(this.userApiUrl, {
       take: 9999, // Get only count, not data
       offset: 0,
       filters: []
     }).pipe(
       map(response => {
         // Get total number of users
         const total = response.length;

         // Count active users (isActive: true)
         const active = response.filter(user => user.isActive === true).length;

         // Count blocked users (have active lockout period)
         const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
         const blocked = response.filter(user => {
           if (!user.lockoutStart || !user.lockoutEnd) return false;

           const lockoutStart = user.lockoutStart.split('T')[0];
           const lockoutEnd = user.lockoutEnd.split('T')[0];

           // Check that today is within lockout period
           return today >= lockoutStart && today <= lockoutEnd;
         }).length;

         return {
           total,
           active,
           blocked
         };
       }),
       catchError(error => {
         console.error('Error fetching user stats:', error);
         return of({
           total: 0,
           active: 0,
           blocked: 0
         });
       })
     );
   }

   /**
    * Get only count of active users
    */
   getActiveUsersCount(): Observable<number> {
    return this.http.post<any[]>(this.userApiUrl, {
      take: 0,
      offset: 0,
      sortBy: "createAt",
      sort: 1,
      filters: [
        {
          filterField: "isActive",
          filterValue: "true",
          filterOperator: 1 // Equal
        }
      ]
    }).pipe(
      map(response => response.length),
      catchError(error => {
        console.error('Error fetching active users count:', error);
        return of(0);
      })
    );
  }

  /**
   * Получает только количество заблокированных пользователей
   */
  getBlockedUsersCount(): Observable<number> {
    return this.http.post<any[]>(this.userApiUrl, {
      take: 0,
      offset: 0,
      sortBy: "createAt",
      sort: 1,
      filters: []
    }).pipe(
      map(response => {
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
        return response.filter(user => {
          if (!user.lockoutStart || !user.lockoutEnd) return false;

           const lockoutStart = user.lockoutStart.split('T')[0];
           const lockoutEnd = user.lockoutEnd.split('T')[0];

           // Check that today is within lockout period
           return today >= lockoutStart && today <= lockoutEnd;
         }).length;
       }),
       catchError(error => {
         console.error('Error fetching blocked users count:', error);
         return of(0);
       })
     );
   }

   /**
    * Extract localized names from object, considering different case variations
    */
   private extractLocalization(entity: any, field: string): LocalizationMap | null {
    if (!entity) {
      return null;
    }

    const direct = entity[field];
    const parsedDirect = this.toLocalizationMap(direct);
    if (parsedDirect) {
      return parsedDirect;
    }

    const pascalCaseKey = field.charAt(0).toUpperCase() + field.slice(1);
    const pascalValue = entity[pascalCaseKey];
    const parsedPascal = this.toLocalizationMap(pascalValue);
    return parsedPascal;
  }

  private extractLocalizationArray(entity: any, field: string): LocalizationMap[] | null {
    if (!entity) {
      return null;
    }

    const direct = entity[field];
    const parsedDirect = this.toLocalizationArray(direct);
    if (parsedDirect) {
      return parsedDirect;
    }

    const pascalCaseKey = field.charAt(0).toUpperCase() + field.slice(1);
    const pascalValue = entity[pascalCaseKey];
    return this.toLocalizationArray(pascalValue);
  }

  private toLocalizationMap(value: any): LocalizationMap | null {
    if (!value) return null;

    if (typeof value === 'object' && value !== null) {
      return value;
    }

    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (typeof parsed === 'object' && parsed !== null) {
          return parsed;
        }
      } catch {
        return null;
      }
    }

    return null;
  }

  private toLocalizationArray(value: any): LocalizationMap[] | null {
    if (!value) {
      return null;
    }

    const normalize = (item: any): LocalizationMap | null => {
      if (!item) {
        return null;
      }

      if (typeof item === 'object') {
        return item;
      }

      if (typeof item === 'string') {
        try {
          const parsed = JSON.parse(item);
          return typeof parsed === 'object' && parsed !== null ? parsed : null;
        } catch {
          return null;
        }
      }

      return null;
    };

    if (Array.isArray(value)) {
      const mapped = value.map(normalize).filter((item): item is LocalizationMap => item !== null);
      return mapped.length > 0 ? mapped : null;
    }

    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          const mapped = parsed.map(normalize).filter((item): item is LocalizationMap => item !== null);
          return mapped.length > 0 ? mapped : null;
        }

        const single = normalize(parsed);
        return single ? [single] : null;
      } catch {
        return null;
      }
    }

    return null;
  }
}
